import type { ApolloClient } from '@apollo/client';
import geolocationService from '../../pickup/services/PickUpGeolocationService';
import { getCurrentServerTimeMillis } from '../../../utils/pdaCameraUtils';

const MAPBOX_TOKEN = 'pk.eyJ1IjoibGl4aWFvaHVpdGl0YSIsImEiOiJjbWNsc2J3aTcwZDkyMmtwdGxiZW95YWt6In0.qoTeK6P5iQ8Mq62KdImwBQ';
const REVERSE_GEOCODE_TIMEOUT_MS = 4000;

/**
 * 将 Mapbox address feature 格式化为 PDA 风格：
 * "650 Federal Blvd,Carteret,NJ 07008,USA"（逗号后无空格，州用简码，国家用 USA）
 * 仅用于英文；其他语言直接用 place_name。
 */
function formatAddressPdaStyle(feature: Record<string, unknown>): string | null {
  const streetNumber = feature.address as string | undefined;
  const streetName = feature.text as string | undefined;
  const context = (feature.context as Record<string, unknown>[] | undefined) ?? [];

  const getCtx = (prefix: string) =>
    context.find(c => typeof c.id === 'string' && c.id.startsWith(prefix));

  const city = getCtx('place')?.text as string | undefined;
  const postcode = getCtx('postcode')?.text as string | undefined;
  const regionCtx = getCtx('region');
  const countryCtx = getCtx('country');

  // "US-NJ" → "NJ"（仅匹配 XX-YY 格式的美式简码）
  const shortCode = regionCtx?.short_code as string | undefined;
  const state = shortCode?.match(/^[A-Z]{2}-([A-Z]+)$/)
    ? shortCode.split('-')[1]
    : (regionCtx?.text as string | undefined);

  // country short_code "us" → "USA"，其余用文本
  const countryShortCode = countryCtx?.short_code as string | undefined;
  const country = countryShortCode === 'us' ? 'USA' : (countryCtx?.text as string | undefined);

  const street = [streetNumber, streetName].filter(Boolean).join(' ');
  const stateZip = [state, postcode].filter(Boolean).join(' ');
  const parts = [street, city, stateZip, country].filter(Boolean);
  return parts.length >= 2 ? parts.join(',') : null;
}

/**
 * Mapbox 反向地理编码：根据坐标获取地址文本。
 * 4 秒超时，失败降级为 null（调用方略去地址行）。
 * language 对应 i18n 语言码（en/zh/es），传给 Mapbox 返回对应语言地址。
 * 英文结果按 PDA 格式重组；其他语言直接使用 place_name。
 */
async function reverseGeocode(lng: number, lat: number, language?: string): Promise<string | null> {
  try {
    const langParam = language ? `&language=${language}` : '';
    // 不限制 types，让 Mapbox 返回最精确的可用结果（街道/社区/城市均可）
    // 若指定 types=address 而当地无街道数据，features 为空会导致地址完全缺失
    const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json?access_token=${MAPBOX_TOKEN}&limit=1${langParam}`;
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), REVERSE_GEOCODE_TIMEOUT_MS);
    const res = await fetch(url, { signal: controller.signal });
    clearTimeout(timer);
    if (!res.ok) {
      console.warn('[Watermark] reverseGeocode HTTP error', res.status);
      return null;
    }
    const json = await res.json();
    const feature = json?.features?.[0] as Record<string, unknown> | undefined;
    console.log('[Watermark] reverseGeocode raw feature', JSON.stringify(feature ?? null));
    if (!feature) return null;
    // 英文且返回的是 address 类型时按 PDA 格式重组；其他情况用 place_name
    const placeTypes = feature.place_type as string[] | undefined;
    const isAddressType = placeTypes?.includes('address') ?? false;
    // language 可能是 "en" 或 "en-US"，统一取前缀判断
    const langPrefix = language?.split('-')[0];
    const result = (!langPrefix || langPrefix === 'en') && isAddressType
      ? formatAddressPdaStyle(feature) ?? (feature.place_name as string | undefined) ?? null
      : (feature.place_name as string | undefined) ?? null;
    console.log('[Watermark] reverseGeocode result', result, '| place_type', placeTypes, '| isAddress', isAddressType);
    return result;
  } catch (e) {
    console.warn('[Watermark] reverseGeocode failed', e);
    return null;
  }
}

interface WatermarkData {
  /** 小时:分钟，12h制，无前导零，如 "8:38" */
  timeStr: string;
  /** "a.m." 或 "p.m." */
  ampm: string;
  /** 如 "Aug 22, 2026" */
  dateStr: string;
  /** 如 "SAT" */
  dayStr: string;
  /** 地址文本，获取失败时为 null */
  address: string | null;
  /** 如 "Lat: 40.582888  Lon: -74.251212"，无 GPS 时为 null */
  coordStr: string | null;
}

const LOCALE_MAP: Record<string, string> = {
  zh: 'zh-CN',
  es: 'es',
  en: 'en-US',
};

/**
 * 将时间戳 + IANA 时区 + GPS 组装为水印所需的结构化数据。
 * language 对应 i18n 语言码（en/zh/es），用于日期/时间的本地化格式。
 */
function buildWatermarkData(
  timestampMs: number,
  ianaTimeZone: string | null | undefined,
  latitude: number | null,
  longitude: number | null,
  address: string | null,
  language?: string,
): WatermarkData {
  const tz = ianaTimeZone || undefined;
  const date = new Date(timestampMs);
  // i18n.language 可能是 "zh-CN"/"en-US" 等完整 BCP-47，先精确匹配，再用前缀（"zh"）兜底
  const locale = (language && (LOCALE_MAP[language] ?? LOCALE_MAP[language.split('-')[0]])) ?? 'en-US';

  // 时间：formatToParts 提取 hour / minute / dayPeriod
  const timeParts = new Intl.DateTimeFormat(locale, {
    timeZone: tz,
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).formatToParts(date);
  const getTime = (type: string) => timeParts.find(p => p.type === type)?.value ?? '';

  // 英文 dayPeriod 是 'AM'/'PM'，统一转为 'a.m.'/'p.m.'；其他语言保留原值（如"上午"/"下午"）
  const dayPeriodRaw = getTime('dayPeriod');
  const dayPeriodLower = dayPeriodRaw.toLowerCase();
  const ampm = locale === 'en-US'
    ? (dayPeriodLower === 'am' ? 'a.m.' : dayPeriodLower === 'pm' ? 'p.m.' : dayPeriodRaw)
    : dayPeriodRaw;

  // 日期：交由 Intl 按 locale 自动格式化（en-US: "Aug 22, 2026"；zh-CN: "2026年8月22日"；es: "22 ago 2026"）
  const dateStr = new Intl.DateTimeFormat(locale, {
    timeZone: tz,
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(date);

  // 星期：short 格式后转大写（en: "SAT"；zh-CN: "周六"；es: "SÁB."）
  const dayStr = new Intl.DateTimeFormat(locale, {
    timeZone: tz,
    weekday: 'short',
  }).format(date).toUpperCase();

  return {
    timeStr: `${getTime('hour')}:${getTime('minute')}`,
    ampm,
    dateStr,
    dayStr,
    address,
    coordStr:
      latitude != null && longitude != null
        ? `Lat: ${latitude.toFixed(6)}  Lon: ${longitude.toFixed(6)}`
        : null,
  };
}

/**
 * 在 Canvas 左下角绘制与 PDA 对齐的水印：
 *   [大时间 a.m.]  │  [日期]
 *                  │  [星期]
 *   [地址]
 *   [Lat/Lon]
 * 底部渐变暗影保证文字在浅色图片上可读。
 */
function drawWatermark(canvas: HTMLCanvasElement, data: WatermarkData): void {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  // 大字号：对齐 PDA Android 比例（约 canvas.width / 18）
  const large = Math.round(canvas.width / 18);
  // 基准字号：大字的 37%（日期/地址/坐标等）
  const base = Math.round(large * 0.37);
  // 小字号："a.m." 约大字的 30%
  const small = Math.round(large * 0.3);
  const marginLeft = Math.round(canvas.width * 0.035);    // 左边距：宽度 3.5%
  const marginBottom = Math.round(canvas.height * 0.09);  // 底边距：高度 9%
  const lineGap = Math.round(base * 1.2);         // 日期/星期行间距
  const timeToAddrGap = Math.round(base * 2.5);   // 时间块与地址之间的间距
  const extraLineStep = Math.round(base * 2.2);   // 地址/坐标行间距
  const colGap = Math.round(base * 1.6);
  const ampmGap = Math.round(base * 0.25); // "8:38" 和 "a.m." 之间的水平间距
  const sepW = Math.max(2, Math.round(base * 0.15));
  const BLUE = '#1D70F4';

  ctx.textBaseline = 'top';
  ctx.textAlign = 'left';

  // ── 测量左区块（"8:38" + " " + "a.m." 同行）──
  ctx.font = `bold ${large}px sans-serif`;
  const timeW = ctx.measureText(data.timeStr).width;

  ctx.font = `${small}px sans-serif`;
  const ampmW = ctx.measureText(data.ampm).width;

  // 左区块总宽 = 时间宽 + 间距 + am/pm宽
  const leftSectionW = timeW + ampmGap + ampmW;
  // 左区块高度 = 大字高度（am/pm 对齐底部，不占额外高度）
  const timeBlockH = large;

  // ── 测量右区块 ──
  ctx.font = `${base}px sans-serif`;
  const dateW = ctx.measureText(data.dateStr).width;
  const dayW = ctx.measureText(data.dayStr).width;
  const rightColW = Math.max(dateW, dayW);

  // ── 计算整体高度 ──
  const extraLines: string[] = [];
  if (data.address) extraLines.push(data.address);
  if (data.coordStr) extraLines.push(data.coordStr);

  const extraH = extraLines.length > 0 ? base + (extraLines.length - 1) * extraLineStep : 0;
  const blockH = timeBlockH + (extraLines.length > 0 ? timeToAddrGap + extraH : 0);

  const startX = marginLeft;
  const startY = canvas.height - blockH - marginBottom;

  // ── 底部渐变暗影（确保文字在浅色背景可读）──
  const gradTop = Math.max(0, startY - base * 2);
  const grad = ctx.createLinearGradient(0, gradTop, 0, canvas.height);
  grad.addColorStop(0, 'rgba(0,0,0,0)');
  grad.addColorStop(1, 'rgba(0,0,0,0.35)');
  ctx.fillStyle = grad;
  ctx.fillRect(0, gradTop, canvas.width, canvas.height - gradTop);

  // ── 左区块：大时间 ──
  ctx.fillStyle = '#FFFFFF';
  ctx.font = `bold ${large}px sans-serif`;
  ctx.fillText(data.timeStr, startX, startY);

  // ── "a.m." 在 "8:38" 右侧，顶部对齐（与大字同 Y，右上角效果）──
  ctx.font = `${small}px sans-serif`;
  ctx.fillText(data.ampm, startX + timeW + ampmGap, startY);

  // ── 蓝色竖线（紧随左区块右侧）──
  const sepX = startX + leftSectionW + colGap;
  ctx.fillStyle = BLUE;
  ctx.fillRect(sepX, startY, sepW, timeBlockH + base);

  // ── 右列：日期 + 星期 ──
  const rightX = sepX + sepW + colGap;
  ctx.fillStyle = '#FFFFFF';
  ctx.font = `${base}px sans-serif`;
  ctx.fillText(data.dateStr, rightX, startY);
  ctx.fillText(data.dayStr, rightX, startY + base + lineGap);

  // ── 地址 + 坐标 ──
  let nextY = startY + timeBlockH + timeToAddrGap;
  for (const line of extraLines) {
    ctx.fillText(line, startX, nextY);
    nextY += extraLineStep;
  }
}

/** 可缓存的 GPS + 地址，供同一拍照会话的多张照片复用 */
export interface WatermarkGpsCache {
  latitude: number;
  longitude: number;
  /** Mapbox 反向地理编码结果，失败时为 null */
  address: string | null;
}

export interface AddWatermarkOptions {
  ianaTimeZone?: string | null;
  apolloClient: ApolloClient<unknown>;
  /** 已缓存的 GPS+地址；提供时跳过 getFreshPosition + reverseGeocode，仅获取服务端时间 */
  cachedGps?: WatermarkGpsCache | null;
  /** i18n 语言码（en/zh/es），用于 Mapbox 返回对应语言的地址文本 */
  language?: string;
}

export interface AddWatermarkResult {
  file: File;
  /** 本次使用的 GPS+地址，供调用方缓存后传给下一张照片；GPS 获取失败时为 null */
  gpsCache: WatermarkGpsCache | null;
}

/**
 * iOS 定位不兼容（navigator.geolocation 会一直卡住，且拿不到坐标），
 * 平台级降级：iOS 上完全跳过水印，直接走原图，判断方式与 versionUtils.ts checkWazeSupport 保持一致。
 */
function isIOSDevice(): boolean {
  return /iPad|iPhone|iPod/.test(navigator.userAgent);
}

/**
 * 为图片文件叠加水印，返回带水印的新 File 及本次 GPS 缓存。
 *
 * 水印内容（与 PDA 端对齐）：
 * - 时间：服务端时间（失败降级到设备时间）+ IANA 时区格式化，每次独立获取
 * - GPS 坐标：首次调用通过 getFreshPosition() 获取；提供 cachedGps 时直接复用
 * - 地址：首次调用通过 Mapbox 反向地理编码（4s 超时）获取；提供 cachedGps 时直接复用
 *
 * iOS 上定位不可用，整个水印流程短路，直接返回原图。
 */
export async function addWatermarkToPhoto(
  file: File,
  options: AddWatermarkOptions,
): Promise<AddWatermarkResult> {
  if (isIOSDevice()) {
    return { file, gpsCache: null };
  }

  const { ianaTimeZone, apolloClient, cachedGps, language } = options;

  let latitude: number | null = null;
  let longitude: number | null = null;
  let address: string | null = null;
  let gpsCache: WatermarkGpsCache | null = null;
  let serverTimeMillis: number | null = null;

  if (cachedGps) {
    // GPS 已缓存：只获取服务端时间，跳过定位和 Mapbox
    serverTimeMillis = (await getCurrentServerTimeMillis(apolloClient)) ?? null;
    latitude = cachedGps.latitude;
    longitude = cachedGps.longitude;
    address = cachedGps.address;
    gpsCache = cachedGps;
  } else {
    // 首次：并发获取 GPS + 服务端时间
    const [posResult, time] = await Promise.all([
      geolocationService.getFreshPosition().catch(() => null),
      getCurrentServerTimeMillis(apolloClient),
    ]);
    serverTimeMillis = time ?? null;
    if (posResult) {
      latitude = posResult.latitude;
      longitude = posResult.longitude;
      address = await reverseGeocode(posResult.longitude, posResult.latitude, language);
      gpsCache = { latitude, longitude, address };
    }
  }

  const timestampMs = serverTimeMillis ?? Date.now();

  const data = buildWatermarkData(
    timestampMs,
    ianaTimeZone,
    latitude,
    longitude,
    address,
    language,
  );

  return new Promise<AddWatermarkResult>(resolve => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        resolve({ file, gpsCache });
        return;
      }
      ctx.drawImage(img, 0, 0);
      drawWatermark(canvas, data);
      canvas.toBlob(
        blob => {
          const resultFile = blob ? new File([blob], file.name, { type: file.type || 'image/jpeg' }) : file;
          resolve({ file: resultFile, gpsCache });
        },
        file.type || 'image/jpeg',
        0.92,
      );
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      resolve({ file, gpsCache });
    };
    img.src = objectUrl;
  });
}
